import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import OnboardingLabourRatesForm from './OnboardingLabourRateForm'
import { useDispatch } from 'react-redux'
import { FormValues } from 'app/settings/components/LabourRates/New'
import { createLabourRate, loadLabourRateDefaults, updateLabourRateDefaults } from 'app/settings/state/LabourRates/actions'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useDefaultSkipSetting } from '../use-default-skip-setting'
import { OnboardingStepKey } from '@cbgms/api'
import { Subscriptions } from '@carsys/enums/subscription'
import { LabourRateCTA } from './LabourRateCTA'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'

interface StepInputProps {
  basePath: string
}

interface StepProps {
  basePath: string
  onSubmit: (v: FormValues) => void
  modules: Subscriptions[]
}

export const useLabourRateStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, onboardingInfo }) => {
  const [labourRate, setLabourRate] = useState<FormValues | null>(null)
  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.LabourRate
  const stepData = useStepData(stepName, onboardingInfo, labourRate)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const hideSkip = useDefaultSkipSetting(onboardingInfo)

  const onStepSubmit = useCallback(
    (values: FormValues) => {
      setLabourRate(values)
    },
    [setLabourRate]
  )
  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    if (labourRate) {
      const { Data } = await dispatch(createLabourRate(labourRate))
      const defaults = await dispatch(loadLabourRateDefaults())
      if (!defaults.Data.Bodywork || !defaults.Data.Electrical || !defaults.Data.Mechanical) {
        await dispatch(
          updateLabourRateDefaults({
            Bodywork: !defaults.Data.Bodywork ? Data.UUID : defaults.Data.Bodywork,
            Electrical: !defaults.Data.Electrical ? Data.UUID : defaults.Data.Electrical,
            Mechanical: !defaults.Data.Mechanical ? Data.UUID : defaults.Data.Mechanical
          })
        )
      }
    }
  }, [labourRate, dispatch])

  return {
    Component: LabourRatesStep,
    CTAComponent: LabourRateCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      basePath,
      onSubmit: complete.onSubmit,
      modules: onboardingInfo.modules
    },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_add_labour_rate')}</>
}

export const LabourRatesStep: React.FC<defaultStepFormProps & StepProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathLabourRateForm(basePath)} render={() => <OnboardingLabourRatesForm {...rest} />} />
}
